import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useT } from "../i18n";
import LanguageToggle from "../components/LanguageToggle";
import KTBanner from "../components/KTBanner";
import { ArrowRight, FileText, SlidersHorizontal, Lock, Menu, X } from "lucide-react";
import { useImageOverrides, resolveImage } from "../hooks/useImageOverrides";
import { useLandingCopy, resolveCopy } from "../hooks/useLandingCopy";
import { HERO, HOW_IT_WORKS, SCIENCE, REVIEWS } from "../config/landing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEP_ICONS = {
  file: FileText,
  sliders: SlidersHorizontal,
  lock: Lock,
};

/**
 * Public landing page.
 *
 * Visual design mirrors the reference (editorial minimal / KT Milano) but
 * uses only the app's existing content sources:
 *   • Localised strings come from HERO / HOW_IT_WORKS / SCIENCE constants
 *     in /src/config/landing.js — a single flat config file to make text
 *     and image URLs easy to change without touching layout code.
 *   • The salon "For Professionals" surface stays behind
 *     `public_pro_visible` from GET /api/config/pricing (unchanged).
 *     All /salon/*, admin, DB, subscriptions remain intact.
 *   • KTBanner + disclaimer + language toggle continue to work exactly
 *     as before.
 */
export default function Welcome({ lang, setLang, languages }) {
  const tr = useT(lang);
  const imageOverrides = useImageOverrides();
  const landingCopy = useLandingCopy();

  // Public visibility of the Professional/Salon plan on the landing.
  const [proVisible, setProVisible] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/config/pricing`)
      .then((r) => {
        if (!cancelled) setProVisible(!!r?.data?.public_pro_visible);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Admin can override the hero image via /admin/images. If they haven't,
  // the config default is used — which is ALSO the URL preloaded from
  // public/index.html, so there is no old-asset flash on first paint.
  const heroSrc = resolveImage(imageOverrides, "welcome_hero", HERO.image_url);
  const scienceSrc = resolveImage(imageOverrides, "landing_science", SCIENCE.image_url);
  const pick = (obj) => obj[lang] || obj.en;
  // Admin can override the Hero text (headline / sub / cta) via /admin/landing-copy.
  // If not overridden, the value from /src/config/landing.js is used.
  const heroHeadline = resolveCopy(landingCopy, "headline", lang, HERO.headline);
  const heroSub      = resolveCopy(landingCopy, "sub",      lang, HERO.sub);
  const heroCta      = resolveCopy(landingCopy, "cta",      lang, HERO.cta);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="welcome-page">
      {/* ================= HEADER ================= */}
      <header className="border-b border-[#EFEBE3] bg-white/85 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
          <a
            href="https://www.ktmilano.com"
            target="_blank"
            rel="noreferrer"
            className="flex flex-col leading-tight shrink-0"
            data-testid="landing-brand"
          >
            <span className="font-serif text-xl tracking-[0.28em] text-[#1B1B1B]">HAIRTU</span>
            <span className="text-[9px] tracking-[0.24em] uppercase text-[#8A857E] mt-0.5">
              Powered by KT Milano
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="landing-nav">
            <a
              href="#how-it-works"
              className="text-sm text-[#383633] hover:text-black transition-colors"
              data-testid="nav-how"
            >
              {pick(HOW_IT_WORKS.kicker)}
            </a>
            <a
              href="#faq"
              className="text-sm text-[#383633] hover:text-black transition-colors"
              data-testid="nav-faq"
            >
              FAQ
            </a>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <LanguageToggle lang={lang} setLang={setLang} languages={languages} />
            <Link
              to="/assessment"
              data-testid="nav-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#111111] hover:bg-[#000000] text-white text-sm font-medium px-5 py-2.5 transition-all hover:-translate-y-[1px]"
            >
              {heroCta}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileMenu((v) => !v)}
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#E5E1D8] text-[#383633]"
          >
            {mobileMenu ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>

        {/* Mobile menu panel */}
        {mobileMenu && (
          <div className="md:hidden border-t border-[#EFEBE3] bg-white" data-testid="mobile-menu">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">
              <a
                href="#how-it-works"
                className="text-sm text-[#383633] py-2"
                onClick={() => setMobileMenu(false)}
              >
                {pick(HOW_IT_WORKS.kicker)}
              </a>
              <a
                href="#faq"
                className="text-sm text-[#383633] py-2"
                onClick={() => setMobileMenu(false)}
              >
                FAQ
              </a>
              <div className="pt-1">
                <LanguageToggle lang={lang} setLang={setLang} languages={languages} />
              </div>
              <Link
                to="/assessment"
                data-testid="mobile-nav-cta"
                onClick={() => setMobileMenu(false)}
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#111] text-white text-sm font-medium px-5 py-3"
              >
                {heroCta}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ================= HERO ================= */}
      <section
        className="bg-[#F3F3F3]"
        data-testid="welcome-hero"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 sm:py-20 lg:py-24 grid lg:grid-cols-[minmax(0,0.9fr),minmax(0,1.1fr)] gap-5 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1"
          >
            <div
              className="text-[10px] tracking-[0.28em] uppercase text-[#8A857E] leading-relaxed mb-3 sm:mb-6"
              data-testid="welcome-kicker"
            >
              {pick(HERO.eyebrow).map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>

            <h1
              className="font-serif text-[34px] leading-[1.05] sm:text-6xl sm:leading-[1.02] lg:text-[76px] text-[#1B1B1B] tracking-tight mb-3 sm:mb-6"
              data-testid="welcome-title"
            >
              {heroHeadline}
            </h1>

            <p
              className="text-[#3B3B3B] text-sm sm:text-lg leading-relaxed max-w-md mb-5 sm:mb-8"
              data-testid="welcome-sub"
            >
              {heroSub}
            </p>

            <Link
              to="/assessment"
              data-testid="hero-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#111111] hover:bg-[#000000] text-white text-sm sm:text-base font-medium px-6 py-3 sm:px-7 sm:py-4 transition-all hover:-translate-y-[1px]"
            >
              {heroCta}
              <ArrowRight className="h-4 w-4" />
            </Link>

            <div
              className="mt-4 sm:mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#6F6B66]"
              data-testid="landing-trust"
            >
              {pick(HERO.trust).map((t, i) => (
                <span key={i} className="inline-flex items-center gap-6">
                  {i > 0 && <span className="text-[#C7C1B4]">·</span>}
                  <span>{t}</span>
                </span>
              ))}
            </div>

            {/* Preserved fallback — surfaces the Professional/Salon entry
                only when admin flips public_pro_visible=true. Salon routes,
                admin panel, DB unchanged. */}
            {proVisible && (
              <div className="mt-6" data-testid="entry-professional-wrap">
                <Link
                  to="/salon"
                  data-testid="entry-professional"
                  className="text-xs kt-tracking uppercase text-[#383633] underline underline-offset-4 hover:text-black"
                >
                  {tr.entry_professional_cta || "Professional access"}
                </Link>
              </div>
            )}
          </motion.div>

          {/* Hero image — right column */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="order-1 lg:order-2"
          >
            <div className="relative rounded-3xl overflow-hidden bg-[#F3F3F3] aspect-[16/10] sm:aspect-[4/5] lg:aspect-[5/6] w-full">
              <img
                src={heroSrc}
                alt=""
                width="1200"
                height="1500"
                fetchpriority="high"
                loading="eager"
                decoding="sync"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= REVIEWS / TESTIMONIALS ================= */}
      <section className="bg-white" data-testid="landing-reviews">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 sm:py-20 lg:py-24">
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A857E] mb-3" data-testid="reviews-kicker">
              {pick(REVIEWS.kicker)}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-[#1B1B1B]" data-testid="reviews-headline">
              {pick(REVIEWS.headline)}
            </h2>
          </div>

          <div className="grid gap-5 sm:gap-6 lg:grid-cols-3">
            {REVIEWS.items.map((r, i) => {
              const quote = (r.quote && (r.quote[lang] || r.quote.en)) || "";
              const name  = (r.name  && (r.name[lang]  || r.name.en))  || "";
              const city  = (r.city  && (r.city[lang]  || r.city.en))  || "";
              return (
                <figure
                  key={i}
                  data-testid={`review-card-${i}`}
                  className="relative bg-[#F3F3F3] rounded-3xl p-6 sm:p-7 flex flex-col justify-between h-full"
                >
                  {/* subtle serif open-quote mark */}
                  <div aria-hidden className="font-serif text-5xl leading-none text-[#D8B589] mb-2 select-none">“</div>
                  <blockquote className="text-[15px] sm:text-base leading-relaxed text-[#2C2A26]">
                    {quote}
                  </blockquote>
                  <figcaption className="mt-6 flex items-center gap-3">
                    <div
                      aria-hidden
                      className="h-9 w-9 rounded-full bg-[#E7E1D5] text-[#5B4A2E] font-serif text-sm flex items-center justify-center shrink-0"
                    >
                      {(name || "•").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="text-[12px] leading-tight">
                      <div className="text-[#1B1B1B] font-medium">{name}</div>
                      {city && <div className="text-[#6F6B66]">{city}</div>}
                    </div>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section
        id="how-it-works"
        className="bg-[#F3F3F3]"
        data-testid="landing-how"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 sm:py-20">
          <h2 className="text-center font-serif text-3xl sm:text-4xl text-[#1B1B1B] tracking-tight mb-12 sm:mb-16">
            {pick(HOW_IT_WORKS.kicker)}
          </h2>

          {/* Desktop — 3 columns with arrows between */}
          <div className="hidden md:flex items-start justify-between gap-6">
            {HOW_IT_WORKS.steps.map((step, i) => {
              const Icon = STEP_ICONS[step.icon] || FileText;
              return (
                <div key={i} className="flex items-start gap-4 flex-1">
                  <div className="flex flex-col items-center text-center flex-1">
                    <div className="h-16 w-16 rounded-full bg-[#E5E5E5] flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-[#5B564C]" />
                    </div>
                    <div className="text-xs kt-tracking uppercase text-[#8A857E] mb-2">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <p
                      className="text-sm text-[#383633] leading-relaxed max-w-[180px]"
                      data-testid={`landing-step-${i + 1}`}
                    >
                      {pick(step.title)}
                    </p>
                  </div>
                  {i < HOW_IT_WORKS.steps.length - 1 && (
                    <div className="pt-6 text-[#B5AE9F]">
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Mobile — stacked with left icon */}
          <div className="md:hidden flex flex-col gap-6">
            {HOW_IT_WORKS.steps.map((step, i) => {
              const Icon = STEP_ICONS[step.icon] || FileText;
              return (
                <div
                  key={i}
                  className="flex items-start gap-4"
                  data-testid={`landing-step-mobile-${i + 1}`}
                >
                  <div className="h-14 w-14 shrink-0 rounded-full bg-[#E5E5E5] flex items-center justify-center">
                    <Icon className="h-5 w-5 text-[#5B564C]" />
                  </div>
                  <div className="pt-1">
                    <div className="text-[10px] kt-tracking uppercase text-[#8A857E] mb-1">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <p className="text-sm text-[#383633] leading-relaxed">{pick(step.title)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= SCIENCE-BACKED CARE ================= */}
      <section className="bg-[#F3F3F3]" data-testid="landing-science">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 items-stretch">
          <div className="relative bg-[#F3F3F3] min-h-[280px] sm:min-h-[360px] lg:min-h-[520px]">
            <img
              src={scienceSrc}
              alt=""
              width="1400"
              height="1000"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="science-image"
            />
          </div>
          <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A857E] mb-4">
              {pick(SCIENCE.kicker)}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1B1B1B] tracking-tight leading-tight mb-5">
              {pick(SCIENCE.headline)}
            </h2>
            <p className="text-sm sm:text-base text-[#4B4741] leading-relaxed max-w-md mb-8">
              {pick(SCIENCE.sub)}
            </p>
            <Link
              to="/assessment"
              data-testid="science-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#111111] hover:bg-[#000000] text-white text-sm font-medium px-6 py-3.5 w-fit transition-all hover:-translate-y-[1px]"
            >
              {heroCta}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Existing KT Milano brand banner — kept untouched at bottom of page. */}
      <section className="bg-white border-t border-[#EFEBE3]" data-testid="landing-brand-band">
        <div className="max-w-3xl mx-auto px-6 py-14">
          <KTBanner size="small" lang={lang} />
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer
        id="faq"
        className="bg-white border-t border-[#EFEBE3]"
        data-testid="landing-footer"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="font-serif text-lg tracking-[0.28em] text-[#1B1B1B]">HAIRTU</div>
            <div className="text-[9px] tracking-[0.24em] uppercase text-[#8A857E] mt-0.5">
              Powered by KT Milano
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#383633]">
            <a
              href="https://www.ktmilano.com/policies/privacy-policy"
              target="_blank"
              rel="noreferrer"
              className="hover:text-black"
              data-testid="footer-privacy"
            >
              Privacy Policy
            </a>
            <a
              href="https://www.ktmilano.com/policies/terms-of-service"
              target="_blank"
              rel="noreferrer"
              className="hover:text-black"
              data-testid="footer-terms"
            >
              Terms of Service
            </a>
            <LanguageToggle lang={lang} setLang={setLang} languages={languages} />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-6 text-[10px] text-[#B5AE9F] leading-relaxed">
          {tr.disclaimer}
        </div>
      </footer>
    </div>
  );
}
